/**
 * Google Cast wiring.
 *
 * Casting is off unless a receiver application id is baked in at build time.
 * That's deliberate: the id comes from the Cast Developer Console and is tied to
 * a specific deployed receiver URL, so a build that doesn't know its own id
 * cannot possibly cast — better to hide the button than to offer one that opens
 * a device picker and then fails.
 */

/** Receiver app id from the Cast Developer Console. Absent → casting disabled. */
export const CAST_APP_ID: string | null = import.meta.env.VITE_CAST_APP_ID || null

export const CAST_ENABLED = CAST_APP_ID !== null

/**
 * Our private channel between phone and television.
 *
 * A custom receiver is a web page, not a media player, so nothing about the room
 * travels in the standard media namespaces — the sender pushes `{ roomCode }`
 * over this one and the receiver attaches itself to that room. Cast requires the
 * `urn:x-cast:` prefix and a reverse-DNS-ish tail.
 */
export const CAST_NAMESPACE = "urn:x-cast:party.misquote.room"

export interface CastRoomMessage {
    roomCode: string
}

/** The Cast receiver runs our own /tv page; this marks it so the page knows to
 *  wait for a room over the Cast channel rather than asking for one on screen. */
export const RECEIVER_QUERY_FLAG = "receiver"

/** True inside a Presentation API receiving browsing context — the page the
 *  spec's `navigator.presentation.receiver` is defined for. */
export function isPresentationReceiver(): boolean {
    if (typeof navigator === "undefined") return false
    return navigator.presentation?.receiver != null
}

/** True where a controller can start a presentation (Chromium only, today). */
export function isPresentationSenderSupported(): boolean {
    return typeof window !== "undefined" && typeof window.PresentationRequest === "function"
}

/**
 * True when this page should bring up the **Cast** receiver SDK.
 *
 * Not the same question as "am I a display". A Presentation API receiving
 * context is also a display, but in 1-UA mode it's a page rendered inside
 * somebody's own browser — starting CAF there leaves it retrying the
 * Chromecast's local agent on `ws://localhost:8008` forever on a laptop that
 * hasn't got one. The Chromecast user-agent is the unambiguous signal; the query
 * flag stands in for devices whose receiver runtime doesn't announce itself that
 * way (Chromecast built-in televisions), but only when we're not already being
 * driven as a presentation.
 */
export function isCastReceiver(): boolean {
    if (typeof window === "undefined") return false
    if (/\bCrKey\b/.test(navigator.userAgent)) return true
    const flagged = new URLSearchParams(window.location.search).get(RECEIVER_QUERY_FLAG) === "1"
    return flagged && !isPresentationReceiver()
}

/**
 * True when this page is being *mirrored* rather than rendered by the display.
 *
 * A Presentation receiving context that isn't running on Cast hardware is a
 * page the controller is rendering offscreen and streaming as video (1-UA).
 * That distinction is worth drawing because the two are not the same medium:
 * one is a screen, the other is a video codec, and things that look like
 * texture to an eye look like noise to an encoder.
 */
export function isMirroredDisplay(): boolean {
    if (typeof navigator === "undefined") return false
    return isPresentationReceiver() && !/\bCrKey\b/.test(navigator.userAgent)
}

/** True when this page was put on a screen by something, whichever transport —
 *  so it waits to be told its room rather than asking for one with a remote. */
export function isDisplayContext(): boolean {
    return isCastReceiver() || isPresentationReceiver()
}

/**
 * The URL a controller asks a display to load.
 *
 * The room travels in the path rather than only over the message channel: a
 * presentation that knows its room the moment it loads has no window where the
 * television sits blank waiting to be told, and reconnecting to it later needs
 * no re-handshake. The channel still carries the code as well, which costs
 * nothing and covers a receiver launched before the code was known.
 */
export function presentationUrl(origin: string, roomCode: string): string {
    return `${origin}/tv/${roomCode}?${RECEIVER_QUERY_FLAG}=1`
}
